import request from "../../utils/request";
import { Result } from "../../models/general";

/**
 * CO-953: how a row names a person without its user id (a system key the server no longer sends).
 * `Unknown` = platform scope only: an id outside the Operator's view.
 */
export type SecurityAuditPersonKind = "System" | "Person" | "Support" | "FormerEmployee" | "Unknown";

/** CO-322 — one row of the append-only security audit log (server: SecurityAuditEventView). */
export interface SecurityAuditEvent {
  id: number;
  occurredUtc: string;
  /** Absent on an older API that predates CO-953 (3); the page then labels from name / staff no. only. */
  actorKind?: SecurityAuditPersonKind;
  /** Only resolved for users inside the caller's scope; null otherwise. */
  actorName?: string | null;
  /** Staff number, shown to company callers when the name is missing (never the user id). */
  actorStaffNo?: string | null;
  actorRoles?: string | null;
  companyId?: number | null;
  eventTypeCode: number;
  eventType: string;
  outcome: "Success" | "Denied" | "Failed" | string;
  targetType?: string | null;
  targetId?: string | null;
  /** CO-953: as `actorKind`, for the subject. */
  targetKind?: SecurityAuditPersonKind;
  targetName?: string | null;
  /** Staff number, shown to company callers when the name is missing (never the user id). */
  targetStaffNo?: string | null;
  httpMethod?: string | null;
  path?: string | null;
  ip?: string | null;
  userAgent?: string | null;
  correlationId?: string | null;
  detailsJson?: string | null;
  hash: string;
}

export interface SecurityAuditPage {
  items: SecurityAuditEvent[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  /** The scope the SERVER applied: true = platform rows (Operator with no company), raw record ids and paths kept; false = one company's rows, GUIDs masked. User ids are never sent. */
  platformScope?: boolean;
}

export interface SecurityAuditEventType {
  code: number;
  name: string;
}

/** Query of GET api/security-audit. Dates are yyyy-MM-dd (UTC, inclusive). Actor / target accept a user id or an email. */
export interface SecurityAuditQuery {
  from?: string;
  to?: string;
  eventType?: number;
  outcome?: number;
  actor?: string;
  targetUser?: string;
  pageNumber: number;
  pageSize: number;
}

function clean(query: SecurityAuditQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = v as string | number;
  });
  return out;
}

/** HR/Admin: own company only. Operator: platform events only. Everyone else: 403. The read itself is audited. */
export async function getSecurityAuditEvents(query: SecurityAuditQuery) {
  const response = await request.get<Result<SecurityAuditPage>>("security-audit", { params: clean(query) });
  return response.data;
}

export async function getSecurityAuditEventTypes() {
  const response = await request.get<Result<SecurityAuditEventType[]>>("security-audit/event-types");
  return response.data;
}

/** CSV of exactly the page described by `query`. The export is audited server-side. */
export async function exportSecurityAuditPage(query: SecurityAuditQuery): Promise<Blob> {
  const response = await request.get("security-audit/export", { params: clean(query), responseType: "blob" });
  return response.data as Blob;
}
